import {
  Card,
  ChevronLeftIcon,
  ContentFooterActions,
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  IconButtonSection,
  IconSizeEnum,
  SpinnerIcon,
  Typography,
  UploadFeeIcon,
} from '@acceso/design-system';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { InputDate } from '@/components/FormFields/InputDate';
import { InputLogos } from '@/components/FormFields/InputLogos';
import { RowContainer } from '@/components/FormFields/RowContainer';
import { SelectForm } from '@/components/FormFields/SelectForm';
import { MEGABYTES_TO_BYTES } from '@/constants';
import { useChannelMediaTypeOptions } from '@/features/broadcasters/hooks/useChannelMediaTypeOptions';
import { useChannelScopeOptions } from '@/features/broadcasters/hooks/useChannelScopeOptions';
import { EconomicalValueProviderEnum } from '@/features/broadcasters/types';
import { useLocationOptions } from '@/features/locations/hooks/useLocationOptions';
import { routerManager } from '@/routes/app-routes';
import { AVRouteEnum } from '@/routes/constants/AVRouteEnum';

import { usePatchEconomicalValues } from '../api/patch-economical-values-file';
import { useEconomicalValuePermissions } from '../hooks/useEconomicalValuePermissions';
import { TEconomicalValuesForm } from '../types';

export const EconomicalValuesPage = () => {
  const { t } = useTranslation();
  const form = useForm<TEconomicalValuesForm>({
    defaultValues: {
      provider: undefined,
      scope: undefined,
      country_code: '',
      region_code: '',
      start_datetime: undefined,
      media_type: undefined,
    },
  });
  const { country_code: countryCode } = useWatch({ control: form.control });

  const media_type = useChannelMediaTypeOptions();
  const { countryOptions, regionOptions } = useLocationOptions({ countryCode });
  const scope = useChannelScopeOptions();
  const { mutate: patchEconomicalValues, isPending, uploadProgress } = usePatchEconomicalValues();

  const handleSubmit = form.handleSubmit(data => {
    patchEconomicalValues(data);
  });

  const { canCreate } = useEconomicalValuePermissions();

  return (
    <>
      <div className="flex items-center justify-between py-size-8">
        <Typography
          fontSize={FontSizeEnum.Xl}
          weight={FontWeightEnum.Bold}
          color={FontColorEnum.Dark10}
        >
          {t('Cargar fichero de tarifas')}
        </Typography>

        <IconButtonSection
          onClick={() => routerManager.to(AVRouteEnum.ECONOMIC_VALUE_CHANNEL_SOURCES)}
          internalProps={{
            icon: {
              size: IconSizeEnum.SM,
              type: ChevronLeftIcon,
            },
            text: {
              value: t('Fuentes de valor económico'),
              color: FontColorEnum.Default,
            },
          }}
        />
      </div>
      <Card>
        <div className="flex items-center px-5">
          <div>
            {isPending ? (
              <div className="flex w-[260px] flex-col items-center">
                <SpinnerIcon size={IconSizeEnum.CUSTOM} className="mb-4 size-40" />
                <Typography
                  className="mb-4 text-justify"
                  fontSize={FontSizeEnum.Xl}
                  weight={FontWeightEnum.Bold}
                  color={FontColorEnum.Secondary}
                >
                  {t('Cargando...')}
                </Typography>
                <Typography
                  className="mb-2 text-center"
                  fontSize={FontSizeEnum.Sm}
                  weight={FontWeightEnum.Regular}
                  color={FontColorEnum.Dark10}
                >
                  {t(
                    'El proceso puede tardar unos minutos, no cierre esta página hasta que el archivo se cargue completamente.'
                  )}
                </Typography>
                <Typography
                  className="text-center"
                  fontSize={FontSizeEnum.Sm}
                  weight={FontWeightEnum.Regular}
                  color={FontColorEnum.Dark10}
                >
                  {t('Puede seguir trabajando abriendo otra página del navegador.')}
                </Typography>
                <Typography
                  className="mt-4 text-center"
                  fontSize={FontSizeEnum.Lg}
                  weight={FontWeightEnum.Regular}
                  color={FontColorEnum.Dark10}
                >
                  {uploadProgress ? `${uploadProgress}%` : ''}
                </Typography>
              </div>
            ) : (
              <div className="flex flex-col items-center p-[120px]">
                <UploadFeeIcon size={IconSizeEnum.CUSTOM} className="mb-4" />
                <Typography
                  fontSize={FontSizeEnum.Xl}
                  weight={FontWeightEnum.Bold}
                  color={FontColorEnum.Dark10}
                  className="whitespace-nowrap"
                >
                  {t('Cargar un nuevo archivo')}
                </Typography>
              </div>
            )}
          </div>
          <div className="flex w-full flex-col gap-4">
            <RowContainer>
              <SelectForm
                options={[{ label: 'Mecos', value: EconomicalValueProviderEnum.MECOS }]}
                form={form}
                name="provider"
                required
                label={t('Proveedor')}
                placeholder={t('Seleccionar...')}
              />
              <SelectForm
                options={scope}
                form={form}
                required
                name="scope"
                label={t('Ámbito')}
                placeholder={t('Seleccionar...')}
              />
            </RowContainer>
            <RowContainer>
              <SelectForm
                options={countryOptions}
                form={form}
                required
                name="country_code"
                label={t('País')}
                placeholder={t('Seleccionar...')}
              />
              <SelectForm
                options={regionOptions}
                form={form}
                name="region_code"
                label={t('Región')}
                placeholder={t('Seleccionar...')}
              />
            </RowContainer>
            <RowContainer>
              <SelectForm
                options={media_type}
                form={form}
                required
                name="media_type"
                label={t('Soporte')}
                placeholder={t('Seleccionar...')}
              />
              <InputDate
                form={form}
                name="start_datetime"
                label={t('Fecha de inicio de periodo')}
                showTimeField={false}
              />
            </RowContainer>

            <InputLogos
              singleFile
              form={form}
              name="file"
              newFiles
              required
              label={t('Seleccionar archivos')}
              format={t('imagen')}
              maxSize={25 * MEGABYTES_TO_BYTES}
            />
          </div>
        </div>
        {canCreate && (
          <ContentFooterActions
            onClose={() => form.reset()}
            actionLabel={t('Cargar')}
            linkLabel={t('Borrar')}
            onAction={() => handleSubmit()}
            disabled={isPending}
          />
        )}
      </Card>
    </>
  );
};
